import { Box } from '@vp/swan';
import React, { useState, useEffect, useMemo } from 'react';
import usePricingContextInitialization from './src-hooks-usePricingContextInitialization.js';
import { useProductStore } from './src-store-productPriceDetails.store.js';
import { PricePresentationType } from './src-types-prices.js';
import { getPresentationType } from './src-util-getPresentationType.js';
import { getPriceQueryKey } from './src-util-price-extraction.js';
import PriceLoading from './src-components-PriceLoading.js';
import { PricePresentation } from './src-components-PricePresentation-PricePresentation.js';
import { SurchargeMessage } from './src-components-SurchargeMessage-SurchargeMessage.js';

const PriceExtractionWrapper = ({
  mpvId,
  productKey,
  version,
  selections,
  quantity,
  cacheableQuantities,
  requestedDisplayStrategy,
  hideSetupFee,
  hidePerUnitPrice,
  hideVolumeDiscount,
  hideMinimumOrderableQuantity
}) => {
  const addPriceQuery = useProductStore((state) => state.addPriceQuery);
  const basePriceExtractionConfig = useProductStore((state) => state.basePriceExtractionConfig);
  const vatIncl = useProductStore((state) => state.vatIncl) || false;
  const { locale = "", tenant = "", developmentMode = false } = basePriceExtractionConfig || {};
  const [pageId, setPageId] = useState();
  const [lastValidPriceQueryKey, setLastValidPriceQueryKey] = useState(void 0);
  const [lastValidQuantity, setLastValidQuantity] = useState(quantity);
  useEffect(() => {
    if (!pageId && basePriceExtractionConfig?.pageId) {
      setPageId(basePriceExtractionConfig?.pageId);
    }
  }, [basePriceExtractionConfig, pageId]);
  const pricingContextInitialized = usePricingContextInitialization(
    tenant,
    locale,
    developmentMode
  );
  const priceQueryKey = getPriceQueryKey({
    mpvId,
    selections,
    quantity,
    cacheableQuantities,
    productKey,
    version
  }) || "";
  const currentProductPriceDetails = useProductStore(
    (state) => state.productPriceDetails?.[priceQueryKey]
  );
  const isError = useProductStore(
    (state) => state.isError
  );
  const lastValidProductPriceDetails = useProductStore(
    (state) => lastValidPriceQueryKey ? state.productPriceDetails?.[lastValidPriceQueryKey] : void 0
  );
  useEffect(() => {
    if (!pricingContextInitialized || !basePriceExtractionConfig || !pageId) return;
    addPriceQuery({
      mpvId,
      productKey,
      version,
      selections,
      quantity,
      cacheableQuantities,
      requestedDisplayStrategy
    }, pageId);
  }, [
    addPriceQuery,
    mpvId,
    quantity,
    cacheableQuantities,
    selections,
    requestedDisplayStrategy,
    productKey,
    version,
    vatIncl,
    pricingContextInitialized,
    pageId
  ]);
  useEffect(() => {
    if (currentProductPriceDetails?.prices?.estimatedPrices) {
      setLastValidPriceQueryKey(priceQueryKey);
      setLastValidQuantity(quantity);
    }
  }, [currentProductPriceDetails, priceQueryKey, quantity]);
  const priceDetailsToUse = currentProductPriceDetails?.prices?.estimatedPrices ? currentProductPriceDetails : lastValidProductPriceDetails;
  const hasProductPriceDetails = priceDetailsToUse?.prices && priceDetailsToUse?.prices.estimatedPrices;
  const estimatedPrices = priceDetailsToUse?.prices?.estimatedPrices ?? {};
  const { minimumOrderableQuantity, maximumOrderableQuantity, defaultQuantity } = priceDetailsToUse?.quantityMetaData ?? {};
  const fallbackQuantity = defaultQuantity ?? minimumOrderableQuantity ?? Number(Object.keys(estimatedPrices)[0]);
  const isRangeStrategy = priceDetailsToUse?.displayStrategy === PricePresentationType.UnitRangePrice;
  const getEvaluatedQuantity = () => {
    if (isRangeStrategy) return minimumOrderableQuantity ?? fallbackQuantity;
    if (currentProductPriceDetails?.prices?.estimatedPrices && quantity && quantity > 0) return quantity;
    if (lastValidQuantity && lastValidQuantity > 0) return lastValidQuantity;
    return fallbackQuantity;
  };
  const evaluatedQuantity = getEvaluatedQuantity();
  const isValidQuantity = typeof evaluatedQuantity === "number" && !isNaN(evaluatedQuantity);
  const isPpagTailProductPage = priceDetailsToUse?.displayStrategy === PricePresentationType.UnitRangePrice || priceDetailsToUse?.displayStrategy === PricePresentationType.PriceWithQtyAndUnit;
  const resolvedPrices = useMemo(() => {
    if (!priceDetailsToUse || !isValidQuantity) return null;
    const displayStrategy = priceDetailsToUse?.displayStrategy;
    const isUnitRangePrice = displayStrategy === PricePresentationType.UnitRangePrice;
    if (isUnitRangePrice && minimumOrderableQuantity && maximumOrderableQuantity) {
      const priceAtMin = estimatedPrices[minimumOrderableQuantity];
      const priceAtMax = estimatedPrices[maximumOrderableQuantity];
      return {
        highestListPrice: priceAtMin?.unitListPrice,
        highestDiscountPrice: priceAtMin?.unitDiscountedPrice,
        lowestListPrice: priceAtMax?.unitListPrice,
        lowestDiscountPrice: priceAtMax?.unitDiscountedPrice,
        setupFee: priceAtMin?.subtotalAmounts?.setupFee?.discountedPrice ?? { taxed: 0, untaxed: 0 }
      };
    }
    let currentPriceInfo = estimatedPrices[evaluatedQuantity];
    if (displayStrategy === PricePresentationType.PriceWithQtyAndUnit && minimumOrderableQuantity) {
      const priceAtMin = estimatedPrices[minimumOrderableQuantity];
      currentPriceInfo = {
        ...currentPriceInfo,
        unitListPriceForMoq: priceAtMin?.unitListPrice ?? currentPriceInfo?.unitListPrice
      };
    }
    return currentPriceInfo;
  }, [
    priceDetailsToUse?.displayStrategy,
    estimatedPrices,
    evaluatedQuantity,
    minimumOrderableQuantity,
    maximumOrderableQuantity
  ]);
  const isMissingResolvedPrices = priceDetailsToUse?.isError === false && !resolvedPrices;
  if (isError || priceDetailsToUse?.isError || isMissingResolvedPrices) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null);
  }
  if (pricingContextInitialized && basePriceExtractionConfig && hasProductPriceDetails && pageId) {
    const priceProperties = {
      minimumFractionDigits: priceDetailsToUse.prices.fractionDigits,
      locale,
      currency: priceDetailsToUse.prices.currency,
      vatInc: Boolean(vatIncl),
      quantity: evaluatedQuantity,
      hideSetupFee,
      hidePerUnitPrice,
      hideVolumeDiscount,
      hideMinimumOrderableQuantity
    };
    if (priceDetailsToUse?.displayStrategy === PricePresentationType.PriceWithQtyAndUnit) {
      const taxed = estimatedPrices[evaluatedQuantity]?.subtotalAmounts?.setupFee?.listPrice?.taxed;
      priceProperties.isSetupFeeIncluded = !!taxed && taxed !== 0;
    }
    return /* @__PURE__ */ React.createElement(Box, { "aria-live": "polite", "aria-atomic": "true", "data-testid": "price-extraction-wrapper-test-id" }, /* @__PURE__ */ React.createElement(
      PricePresentation,
      {
        presentationType: getPresentationType(isPpagTailProductPage, priceDetailsToUse.displayStrategy, pageId),
        prices: resolvedPrices,
        priceProperties,
        pageId
      }
    ), isPpagTailProductPage && productKey && version && /* @__PURE__ */ React.createElement(SurchargeMessage, { locale, productKey, version, pageId }));
  }
  return /* @__PURE__ */ React.createElement(PriceLoading, null);
};

export { PriceExtractionWrapper };
//# sourceMappingURL=src-components-PriceExtraction-PriceExtractionWrapper.js.map
